import React, { createContext, Suspense, useContext, useEffect, useState } from "react";
import ReactPixel from "react-facebook-pixel";
import GAnalytic from "react-ga4";
import TagManager from "react-gtm-module";
import { withRouter } from "react-router-dom";

import {
	cookieConsentWasResolved,
	getCookieConsent,
	hasMarketingConsent,
	hasStatisticsConsent,
	isCookiebotEnabled,
	openCookieSettings,
	runCookiebotScripts,
} from "@utils/cookieConsent";

const CookiePolicyDrawer = React.lazy(() => import("@components/CookiePolicyDrawer"));

const isDefaultDrawerEnabled = () =>
	window.HAS_COOKIE_POLICY_DRAWER === true || window.HAS_COOKIE_POLICY_DRAWER === "true";

const isCookieControlFeatureEnabled = () => isCookiebotEnabled() || isDefaultDrawerEnabled();

let toggleConsent = () => {};

let getCookie = () => getCookieConsent();

let cookieAccepted = () => getCookieConsent();

let cookieWasSet = () => cookieConsentWasResolved();

const CookieContext = createContext({
	cookieStatus: getCookie(),
	setCookieStatus: () => {},
	setShowConsent: toggleConsent,
});

const initStatisticsTracking = () => {
	if (window.__statisticsTrackingInitialized) return;
	window.__statisticsTrackingInitialized = true;

	if (window.gaConfig) {
		GAnalytic.initialize(window.gaConfig);
	}
	if (window.gtagConfig) {
		TagManager.initialize({
			gtmId: window.gtagConfig,
		});
	}
};

const initMarketingTracking = () => {
	if (window.__marketingTrackingInitialized) return;
	if (!window.META_PIXEL_ID) return;

	window.__marketingTrackingInitialized = true;
	ReactPixel.init(
		window.META_PIXEL_ID,
		{},
		{
			autoConfig: true,
			debug: false,
		}
	);
	ReactPixel.pageView();
};

const CookieProvider = withRouter(({ children, location }) => {
	const cookiebotMode = isCookiebotEnabled();
	const defaultMode = !cookiebotMode && isDefaultDrawerEnabled();

	const [showConsent, setShowConsent] = useState(() => {
		if (!defaultMode) return false;
		const isDocsPath = /^\/docs(\/|$)/.test(window.location.pathname);
		return !isDocsPath && !cookieAccepted();
	});
	const [cookieStatus, setCookieStatus] = useState(() => getCookie());

	useEffect(() => {
		if (cookiebotMode) return;
		if (!defaultMode) {
			setShowConsent(false);
			return;
		}

		const isDocsPath = /^\/docs(\/|$)/.test(location.pathname);
		if (isDocsPath || cookieAccepted()) {
			setShowConsent(false);
			return;
		}

		setShowConsent(true);
	}, [location.pathname, cookiebotMode, defaultMode]);

	useEffect(() => {
		if (!cookiebotMode) return;
		runCookiebotScripts();
	}, [location.pathname, cookiebotMode]);

	const handleAccept = () => {
		setCookieStatus(true);
		setShowConsent(false);
		localStorage.setItem("_cookie_consent", true);
	};

	const handleDecline = () => {
		setCookieStatus(false);
		setShowConsent(false);
		localStorage.setItem("_cookie_consent", false);
	};

	useEffect(() => {
		toggleConsent = (consent) => {
			if (cookiebotMode) {
				openCookieSettings();
				return;
			}
			if (!defaultMode) return;
			setShowConsent(consent);
		};

		if (typeof window !== "undefined") {
			window.__toggleCookieConsent = toggleConsent;
		}
	}, [cookiebotMode, defaultMode]);

	useEffect(() => {
		if (!cookiebotMode) {
			if (cookieStatus) {
				initStatisticsTracking();
				initMarketingTracking();
			}
			return undefined;
		}

		const syncConsent = () => {
			setCookieStatus(getCookie());
			if (hasStatisticsConsent()) initStatisticsTracking();
			if (hasMarketingConsent()) initMarketingTracking();
		};

		syncConsent();

		window.addEventListener("CookiebotOnConsentReady", syncConsent);
		window.addEventListener("CookiebotOnAccept", syncConsent);
		window.addEventListener("CookiebotOnDecline", syncConsent);

		return () => {
			window.removeEventListener("CookiebotOnConsentReady", syncConsent);
			window.removeEventListener("CookiebotOnAccept", syncConsent);
			window.removeEventListener("CookiebotOnDecline", syncConsent);
		};
	}, [cookiebotMode, cookieStatus]);

	return (
		<CookieContext.Provider value={{ cookieStatus, setCookieStatus, showConsent }}>
			{children}
			{defaultMode && (
				<Suspense fallback={null}>
					<CookiePolicyDrawer
						open={showConsent}
						onAccept={handleAccept}
						onDecline={handleDecline}
					/>
				</Suspense>
			)}
		</CookieContext.Provider>
	);
});

const useCookie = () => {
	const context = useContext(CookieContext);

	if (!context) {
		throw new Error("useCookie must be used within a CookieProvider");
	}

	return context;
};

export { CookieProvider, cookieWasSet, getCookie, isCookieControlFeatureEnabled, toggleConsent, useCookie };
